import React, { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  PRODUCT_HUB_LINK,
  PRODUCT_MENU_GROUPS,
} from "../../utility/marketingProductNav";

const Tag = ({ children }) => (
  <span className="ml-2 text-[10px] font-display font-bold tracking-wide uppercase text-brand-gold-ink">
    {children}
  </span>
);

const MenuLink = ({ to, label, tag, nested = false, onNavigate, className }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={`${className} ${nested ? "pl-7" : "pl-3"} ${
        active ? "text-brand-charcoal font-semibold" : "text-brand-muted"
      }`}
    >
      <span>{label}</span>
      {tag ? <Tag>{tag}</Tag> : null}
    </Link>
  );
};

/**
 * Header Product dropdown. Works on desktop hover/click and in the mobile drawer.
 */
const ProductMenu = ({ variant = "desktop", onNavigate }) => {
  const { pathname } = useLocation();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open || variant !== "desktop") return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, variant]);

  const closeAndNavigate = () => {
    setOpen(false);
    onNavigate?.();
  };

  if (variant === "mobile") {
    return (
      <div className="border-l-4 border-transparent">
        <p className="px-4 pt-3 pb-1 text-xs font-display font-bold tracking-widest uppercase text-brand-muted">
          Product
        </p>
        <MenuLink
          to={PRODUCT_HUB_LINK.to}
          label={PRODUCT_HUB_LINK.label}
          onNavigate={closeAndNavigate}
          className="block px-4 py-2 text-base hover:text-brand-charcoal"
        />
        {PRODUCT_MENU_GROUPS.map((group) => (
          <div key={group.id} className="pb-2">
            {group.to ? (
              <MenuLink
                to={group.to}
                label={group.label}
                onNavigate={closeAndNavigate}
                className="block px-4 py-1 text-xs font-display font-bold tracking-widest uppercase hover:text-brand-charcoal"
              />
            ) : (
              <p className="px-4 py-1 text-xs font-medium text-brand-muted">
                {group.label}
              </p>
            )}
            {group.items.map((item) => (
              <React.Fragment key={item.to}>
                <MenuLink
                  to={item.to}
                  label={item.label}
                  onNavigate={closeAndNavigate}
                  className="block px-4 py-2 text-base hover:text-brand-charcoal"
                />
                {(item.children || []).map((child) => (
                  <MenuLink
                    key={child.to}
                    to={child.to}
                    label={child.label}
                    tag={child.tag}
                    nested
                    onNavigate={closeAndNavigate}
                    className="block px-4 py-2 text-sm hover:text-brand-charcoal"
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        ))}
        <Link
          to="/pricing"
          onClick={closeAndNavigate}
          className="block px-4 py-3 text-base font-medium text-brand-muted hover:text-brand-charcoal border-l-4 border-transparent hover:border-brand-gold-ink"
        >
          Plans
        </Link>
      </div>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className={`px-3 py-2 text-base font-medium transition border-b-2 focus:outline-none focus:underline focus:underline-offset-4 ${
          open
            ? "border-brand-gold-ink text-brand-charcoal"
            : "border-transparent text-brand-charcoal hover:border-brand-gold-ink"
        }`}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        Product
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 w-72 rounded-lg border border-brand-charcoal/10 bg-brand-cream shadow-lg py-3"
        >
          <div className="px-1 pb-2">
            <MenuLink
              to={PRODUCT_HUB_LINK.to}
              label={PRODUCT_HUB_LINK.label}
              onNavigate={closeAndNavigate}
              className="block py-1.5 pr-3 text-sm font-semibold hover:bg-white rounded-md"
            />
          </div>
          {PRODUCT_MENU_GROUPS.map((group) => (
            <div key={group.id} className="px-1 pb-2">
              {group.to ? (
                <MenuLink
                  to={group.to}
                  label={group.label}
                  onNavigate={closeAndNavigate}
                  className="block py-1 pr-3 text-xs font-display font-bold tracking-widest uppercase hover:bg-white rounded-md"
                />
              ) : (
                <p className="px-3 py-1 text-xs font-display font-bold tracking-widest uppercase text-brand-muted">
                  {group.label}
                </p>
              )}
              {group.items.map((item) => (
                <React.Fragment key={item.to}>
                  <MenuLink
                    to={item.to}
                    label={item.label}
                    onNavigate={closeAndNavigate}
                    className="block py-1.5 pr-3 text-sm hover:bg-white rounded-md"
                  />
                  {(item.children || []).map((child) => (
                    <MenuLink
                      key={child.to}
                      to={child.to}
                      label={child.label}
                      tag={child.tag}
                      nested
                      onNavigate={closeAndNavigate}
                      className="block py-1.5 pr-3 text-sm hover:bg-white rounded-md"
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default ProductMenu;
